import { PublicShell } from '@/components/layout/PublicShell';
import { useBranding, usePlatformName } from '@/hooks/useBranding';

export function PrivacyPolicyPage() {
  const name = usePlatformName();
  const { data } = useBranding();
  const supportEmail = data?.branding.support_email || 'bot@workercms.com';
  const today = new Date().toLocaleDateString('tr-TR');

  return (
    <PublicShell>
      <article className="container max-w-3xl py-12 space-y-6 text-sm leading-relaxed">
        <header className="space-y-1">
          <h1 className="text-3xl font-bold">Gizlilik Politikası</h1>
          <p className="text-muted-foreground">Son güncelleme: {today}</p>
        </header>

        <p>
          Bu Gizlilik Politikası, {name} ("Platform", "biz") tarafından hizmetlerimizi kullanırken
          toplanan kişisel verilerin nasıl işlendiğini açıklar. Platformu kullanarak bu politikayı
          kabul etmiş olursunuz.
        </p>

        <Section title="1. Topladığımız Veriler">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Hesap bilgileri:</strong> e-posta adresi, ad-soyad, şifre özeti (hash), organizasyon bilgileri.
            </li>
            <li>
              <strong>Kullanım verileri:</strong> üretilen içerik başlıkları, kredi tüketimi, siteye gönderim günlükleri.
            </li>
            <li>
              <strong>Entegrasyon verileri:</strong> WordPress site URL'si, uygulama şifresi (şifrelenmiş olarak saklanır).
            </li>
            <li>
              <strong>Ödeme verileri:</strong> kripto işlem TXID'leri, cüzdan adresleri. <em>Kredi kartı bilgisi saklamıyoruz.</em>
            </li>
            <li>
              <strong>Teknik veriler:</strong> IP adresi, tarayıcı bilgisi, çerez tanımlayıcıları.
            </li>
          </ul>
        </Section>

        <Section title="2. Verileri Kullanma Amaçlarımız">
          <ul className="list-disc pl-5 space-y-1">
            <li>Hizmeti sunmak, hesabınızı yönetmek, kredi bakiyesini takip etmek.</li>
            <li>AI içerik üretim taleplerini ilgili yapay zeka sağlayıcılarına iletmek.</li>
            <li>WordPress sitelerine yayın göndermek.</li>
            <li>Güvenlik, dolandırıcılık önleme, suistimal tespiti.</li>
            <li>Yasal yükümlülükler çerçevesinde raporlama.</li>
            <li>Hizmet iyileştirme ve teknik sorun giderme.</li>
          </ul>
        </Section>

        <Section title="3. Üçüncü Taraflarla Paylaşım">
          <p>
            Verileriniz aşağıdaki altyapı sağlayıcılarıyla, yalnızca hizmetin işleyişi için paylaşılır:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Cloudflare</strong> — barındırma, veri tabanı (D1), dosya depolama (R2).
            </li>
            <li>
              <strong>AI sağlayıcıları</strong> (AIMLAPI, OpenAI, Anthropic, Google, vb.) — içerik üretimi için gönderdiğiniz prompt ve parametreler.
            </li>
            <li>
              <strong>Bağlı WordPress siteleriniz</strong> — sadece sizin bağladığınız siteler.
            </li>
            <li>
              <strong>Blockchain explorer API'leri</strong> — kripto ödeme doğrulaması için yalnızca TXID sorguları.
            </li>
          </ul>
          <p className="mt-2">
            Kişisel verilerinizi pazarlama amaçlı üçüncü taraflara satmıyoruz.
          </p>
        </Section>

        <Section title="4. Veri Saklama Süresi">
          <p>
            Hesap verileriniz, hesabınız aktif olduğu sürece saklanır. Hesabınızı kapattığınızda
            ödeme ve yasal kayıt gereksinimleri dışındaki veriler 90 gün içinde silinir. Ödeme
            kayıtları, vergi mevzuatı gereği 5 yıl boyunca saklanır.
          </p>
        </Section>

        <Section title="5. Haklarınız (KVKK & GDPR)">
          <ul className="list-disc pl-5 space-y-1">
            <li>İşlenen verilerinize erişim talep etme,</li>
            <li>Yanlış verilerin düzeltilmesini isteme,</li>
            <li>Silinmesini veya anonimleştirilmesini talep etme,</li>
            <li>Veri taşınabilirliği (dışa aktarım) talep etme,</li>
            <li>İşlemeye itiraz etme hakkınız vardır.</li>
          </ul>
          <p className="mt-2">
            Bu haklarınızı kullanmak için{' '}
            <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>{' '}
            adresinden bize ulaşabilirsiniz.
          </p>
        </Section>

        <Section title="6. Çerezler">
          <p>
            Platform, oturum yönetimi ve güvenlik için gerekli çerezleri kullanır. Analitik veya
            pazarlama çerezi kullanmıyoruz. Tarayıcı ayarlarınızdan çerezleri kısıtlayabilirsiniz
            ancak bu durumda bazı özellikler çalışmayabilir.
          </p>
        </Section>

        <Section title="7. Güvenlik">
          <p>
            Şifreler tek yönlü hash ile, API anahtarları ve WordPress kimlik bilgileri AES-GCM ile
            şifreli olarak saklanır. Tüm trafik HTTPS üzerinden aktarılır. Ancak hiçbir sistemin
            %100 güvenli olmadığını belirtmeliyiz.
          </p>
        </Section>

        <Section title="8. Değişiklikler">
          <p>
            Bu politikada değişiklik yapıldığında, hesabınıza kayıtlı e-posta adresinize bildirim
            gönderilecektir. Güncellenmiş politikanın yürürlüğe girme tarihi bu sayfanın üstünde yer alır.
          </p>
        </Section>

        <Section title="9. İletişim">
          <p>
            Gizlilikle ilgili soru ve talepleriniz için:{' '}
            <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
          </p>
        </Section>
      </article>
    </PublicShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}
